import React, { useRef, useLayoutEffect, useCallback } from 'react';
import { TabWrapper, PillIndicator, UnderlineIndicator, TabsRoot } from './Tabs.styles';
import type { TabsVariant } from './Tabs.styles';
import type { ToggleItemProps } from './components/ToggleItem/ToggleItem';
import { ToggleItem } from './components/ToggleItem/ToggleItem';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useTimeout } from '@shared/hooks/useTimeout';
import { useSpring } from 'react-spring';
import type { TextV2Props } from '@withjoy/joykit';

export type TabItemProps = Pick<ToggleItemProps, 'label' | 'value' | 'isActive' | 'onClick' | 'isDisabled'>;

interface Props
  extends Readonly<{
    items: ReadonlyArray<TabItemProps>;
    isMobile?: boolean;
    width?: string;
    tabTextProps?: TextV2Props;
    variant?: TabsVariant;
  }> {}

const Tabs: React.FC<Props> = ({ items, isMobile, width: TabsRootWidth, tabTextProps, variant = 'pill' }) => {
  const isMounted = useIsMounted();
  const timeout = useTimeout();
  const tabsContainer = useRef<HTMLDivElement>(null);
  const pillIndicatorRef = useRef<HTMLSpanElement>(null);
  const underlineIndicatorRef = useRef<HTMLSpanElement>(null);
  const hasInitializedUnderline = useRef(false);

  const [{ left, width: tabWidth }, set] = useSpring(() => ({ width: 0, left: 0 }));

  const updateUnderlineIndicator = useCallback(() => {
    const indicator = underlineIndicatorRef.current;
    const container = tabsContainer.current;
    if (!indicator || !container) return;

    const children = Array.from(container.children) as HTMLElement[];
    const activeTab = children.find(child => child.classList.contains('active'));
    if (!activeTab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const offsetLeft = tabRect.left - containerRect.left;
    const width = tabRect.width;

    if (width === 0) return;

    if (!hasInitializedUnderline.current) {
      indicator.style.transition = 'none';
      hasInitializedUnderline.current = true;
    }

    indicator.style.transform = `translateX(${offsetLeft}px)`;
    indicator.style.width = `${width}px`;

    if (!hasInitializedUnderline.current) return;
    // Force reflow then re-enable transitions after first positioning
    void indicator.offsetHeight;
    indicator.style.transition = '';
  }, []);

  useLayoutEffect(() => {
    if (!isMounted) return;

    if (variant === 'underline') {
      updateUnderlineIndicator();
      return;
    }

    if (pillIndicatorRef.current && tabsContainer.current) {
      const children = Array.from(tabsContainer.current.children);
      const activeTab = (children as HTMLElement[]).find(child => child.classList.contains('active'));
      if (activeTab) {
        // Delay measurement to allow flex layout and translations to complete before calculating indicator width
        timeout.set(() => {
          const { width } = activeTab.getBoundingClientRect();
          set({ left: activeTab.offsetLeft, width });
        }, 50);
      }
    }
  }, [isMounted, items, isMobile, tabTextProps, TabsRootWidth, set, timeout, variant, updateUnderlineIndicator]);

  return (
    <TabsRoot
      width={TabsRootWidth}
      variant={variant}
    >
      <TabWrapper
        ref={tabsContainer}
        variant={variant}
      >
        {items.map(item => (
          <ToggleItem
            key={item.label}
            isMobile={isMobile}
            isDisabled={item.isDisabled}
            textProps={tabTextProps}
            variant={variant}
            {...item}
            onClick={e => {
              if (!item.isActive) {
                item.onClick?.(e);
              }
            }}
          />
        ))}
      </TabWrapper>
      {variant === 'underline' ? (
        <UnderlineIndicator ref={underlineIndicatorRef} />
      ) : (
        <PillIndicator
          ref={pillIndicatorRef}
          style={{ transform: left.interpolate(value => `translate3d(${value}px, -50%, 0px)`), willChange: 'transform', width: tabWidth }}
        />
      )}
    </TabsRoot>
  );
};

export { Tabs };
export type { TabsVariant };
